import { useState, useEffect, useRef } from "react";
import { Spin } from "antd";

/**
 * Overlay modal shown on POS after payment while waiting for customer feedback
 * on the customer display. Auto-closes when:
 *   1. Customer submits feedback (feedbackComplete IPC event)
 *   2. Countdown timer expires
 *   3. Cashier clicks "Skip"
 *
 * Usage: Mount via CustomerDisplayBridge. Listens for "cd:feedbackStart" event.
 */
const FeedbackWaitingModal = () => {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);
  const unsubRef = useRef(null);

  const close = (wasSubmitted = false) => {
    setVisible(false);
    setCountdown(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Cleanup feedback listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    // Return customer display to idle (only if skipped, not if already returned)
    if (!wasSubmitted && window.electronAPI?.plugin?.custDisplay) {
      window.electronAPI.plugin.custDisplay.setMode("idle").catch(() => {});
    }
    // Notify bridge that feedback flow ended
    window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
    // Brief delay then reset submitted state
    setTimeout(() => setSubmitted(false), 300);
  };

  useEffect(() => {
    const handleStart = (e) => {
      const timeout = e.detail?.timeout || 60;
      setCountdown(timeout);
      setSubmitted(false);
      setVisible(true);

      // Start countdown
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            close();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Listen for feedback completion from display
      if (unsubRef.current) unsubRef.current();
      if (window.electronAPI?.plugin?.custDisplay?.onFeedbackComplete) {
        unsubRef.current = window.electronAPI.plugin.custDisplay.onFeedbackComplete(() => {
          setSubmitted(true);
          // Show "received" message briefly then close
          setTimeout(() => close(true), 1500);
        });
      }
    };

    window.addEventListener("cd:feedbackStart", handleStart);
    return () => {
      window.removeEventListener("cd:feedbackStart", handleStart);
      if (timerRef.current) clearInterval(timerRef.current);
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff", borderRadius: 12, padding: "32px 40px",
          textAlign: "center", minWidth: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
        }}
      >
        {submitted ? (
          <>
            <div
              style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "#f0fff4", border: "3px solid #52c41a",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px", fontSize: 32, color: "#52c41a",
              }}
            >
              &#10003;
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>
              Feedback Received
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: "#666" }}>
              Thank you! Closing...
            </p>
          </>
        ) : (
          <>
            <Spin size="large" style={{ marginBottom: 20 }} />
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>
              Waiting for Customer Feedback
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#666" }}>
              Customer display is showing feedback screen
            </p>
            <div
              style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "#f0f5ff", border: "3px solid #A4CD38",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px", fontSize: 28, fontWeight: 700, color: "#2F3856",
              }}
            >
              {countdown}s
            </div>
            <button
              style={{
                height: 40, padding: "0 32px", fontSize: 14, fontWeight: 500,
                borderRadius: 8, background: "#2F3856", color: "#fff", border: "none",
                cursor: "pointer",
              }}
              onClick={() => close(false)}
            >
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackWaitingModal;
